import type { Maybe } from "@/domains/common/types";
import type { Uuid } from "@/domains/global/identifiers";
import { BaseCurrentAssistantSyncModelStore } from "@/store/assistant/base-current-assistant-sync-model-store";
import { AssistantSkillObservable } from "@/store/assistant/AssistantSkillObservable";
import type { AssistantSkillIndexData, AssistantSkillModelData } from "@/store/assistant/types";
import type { AppStore } from "@/store/AppStore";
import { type OptimisticSyncUpdate, SyncModelKind, type SyncUpdateValue } from "@/store/sync/types";
import type { AppSubStoreArgs } from "@/store/types";
import { makeObservable, override } from "mobx";

export class AppStoreAssistantSkillsStore extends BaseCurrentAssistantSyncModelStore<
  AssistantSkillObservable,
  AssistantSkillModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.AssistantSkill, ...injectedDeps });

    makeObservable<this, "createSyncModel">(this, {
      createSyncModel: false,
      computeIndexes: override,
      getAssistantSkillObservableById: false,
    });
  }

  createSyncModel(data: SyncUpdateValue<AssistantSkillModelData>): AssistantSkillObservable {
    return new AssistantSkillObservable({ id: data.model_id, data, store: this.store });
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<AssistantSkillModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<AssistantSkillModelData>[];
  }): AssistantSkillIndexData {
    const syncUpdateValue =
      (optimisticUpdates.at(-1)?.value as SyncUpdateValue<AssistantSkillModelData> | undefined) ||
      remoteData;
    const modelData = syncUpdateValue?.model_data;

    return {
      assistant_id: modelData?.assistant_id ?? "",
      status: modelData?.status ?? "",
      name: modelData?.name ?? "",
      created_at: modelData?.created_at ?? "",
      updated_at: modelData?.updated_at ?? "",
    };
  }

  getAssistantSkillObservableById({
    assistantSkillId,
  }: {
    assistantSkillId: Uuid;
  }): AssistantSkillObservable | undefined {
    return this.get(assistantSkillId);
  }
}
